import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Col from '../../../layout/Col';
import LocModelDropdown from '../../../LocModelDropdown';
import Form from '../../../forms/Form';
import Button from '../../../forms/Button';
import { exportToPDF, generateId, inputDate, offsetUTC, planeDate } from '../../../utility/functions';
import Table from '../../../table/Table';
import HamQuest from '../../../api/HamQuestGiver';
import { QuestRoute } from '../../../api/QuestRoutes';
import Row from '../../../layout/Row';
import Collapsible from '../../../layout/Collapsable';
import Checkbox from '../../../forms/Checkbox';
import Card from '../../../forms/Card';
import Heading from '../../../forms/Heading';
import CompanyLogo from '../../../CompanyLogo';
import Input from '../../../forms/Input';
import ModalFactory from '../../../Modal';
import { SCREEN_WIDTH } from '../../../../constants/config';

const HeaderRow = styled.div`
    display: flex;
    gap: 4em;
    justify-content: space-between;
    align-items: flex-start;
`
const ReportSegment = styled.div`
    border-bottom: 1px solid ${({ theme }) => theme.gray}40;
    margin-bottom: 0.5em;
    padding-bottom: 0.2em;
`

const HourlyUseReport = ({ ...props }) => {

    const hourly = [
        '12AM', '1AM', '2AM', '3AM', '4AM', '5AM', '6AM', '7AM',
        '8AM', '9AM', '10AM', '11AM', '12PM', '1PM', '2PM', '3PM',
        '4PM', '5PM', '6PM', '7PM', '8PM', '9PM', '10PM', '11PM'
    ]

    const cardRef = useRef();
    const [usageData, setUsageData] = useState(null);
    const [locModels, setLocModels] = useState(null);
    const [filteredItems, setFilteredItems] = useState([]);

    const [date, setDate] = useState(inputDate())
    const [reportDate, setReportDate] = useState('')
    const [itemType, setItemType] = useState([])



    useEffect(() => {
        HamQuest.get(QuestRoute.items.hourly)
            .then(res => {
                setItemType(
                    res.sort((a, b) => String(a.label).localeCompare(String(b.label)))
                )
                setFilteredItems(res.map(e => e.value))
            })
            .catch(ModalFactory.catch)
    }, [])

    const TableDetails = ({ data }) => {
        const headers = [
            {
                label: 'Hour',
                propKey: '',
            },
        ]
        const col = [];

        data.locationTotals.forEach(e => {
            const obj = {
                label: e.locationName,
                propKey: '',
            }
            headers.push(obj)
            col.push(obj)
        })

        headers.push({
            label: 'Total',
            propKey: '',
        })

        return (
            <Table
                key='HourlyReport'
                config={{
                    disableExport: true,
                    disableSlideAnimation: true,
                    disableHeaderSearch: true,
                    disablePagerResults: true,
                    disableTableControls: true,
                    disableMobileView: true,
                    hideNoResults: true,
                    data: data.hourlyDataRecords,
                    headers: headers,
                    resultsPerPage: 24,
                    renderFormat: (e, i) => {
                        const { locationCounts } = e;
                        return (
                            <tr key={generateId()}>
                                <td>{hourly[i]}</td>
                                {col.map(f => (
                                    <td>{locationCounts.find(g => g.locationName === f.label)?.count ?? 0}</td>
                                ))}
                                <td>{e.total}</td>
                            </tr>
                        )
                    },
                    finalRow: (e) => {
                        const { locationTotals, totalCount } = data;
                        return (
                            <tr key={generateId()}>
                                <td>Total</td>
                                {col.map(f => (
                                    <td>{locationTotals.find(g => g.locationName === f.label)?.count ?? 0}</td>
                                ))}
                                <td>{totalCount}</td>
                            </tr>
                        )
                    }
                }}
            />
        );
    }

    return (
        <Row align='flex-start' style={SCREEN_WIDTH ? { width: '100%', flexDirection: 'column' } : { width: '100%' }}>
            <Col style={{ width: '100%', flex: '1' }}>
                <Button
                    disabled={!usageData}
                    onClick={() => exportToPDF(cardRef, `Hourly Report`)}
                >Export</Button>
                <Form
                    inputs={{
                        startDate: date,
                    }}
                    onSubmit={(e) => {
                        return HamQuest.post(QuestRoute.reports.hourly, {
                            date,
                            query: locModels.query,
                            items: filteredItems
                        })
                            .then(res => {
                                const backfill = new Array(24).fill().map((e, i) => {
                                    return {
                                        hour: `${i}`,
                                        locationCounts: [],
                                        total: 0
                                    }
                                });
                                res.hourlyDataRecords.forEach(e => backfill[e.hour] = { ...e })
                                res.hourlyDataRecords = backfill;
                                res.selectedItems = [...filteredItems];
                                setReportDate(date)
                                setUsageData(res)
                            })
                    }}
                    text='Filter'
                >
                    <Input
                        label='Start Date'
                        name='startDate'
                        type='date'
                        value={date}
                        onInput={e => setDate(e.target.value)}
                        required
                        standalone
                    />
                    <LocModelDropdown
                        startDate={date}
                        getState={setLocModels}
                    />
                    {itemType && (
                        <Collapsible label={'Items'}>
                            {
                                itemType.map((e, i) => (
                                    <Checkbox
                                        key={`${i}-items`}
                                        label={e.label}
                                        name={'items'}
                                        value={e.value}
                                        checked={filteredItems.includes(e.value)}
                                        style={{ fontSize: '0.5em', }}
                                        inputClick={() => {
                                            setFilteredItems(
                                                filteredItems.includes(e.value)
                                                    ? filteredItems.filter(f => f !== e.value)
                                                    : [...filteredItems, e.value]
                                            )
                                        }}
                                    />
                                ))
                            }
                        </Collapsible>
                    )}
                </Form>
            </Col>
            <Col style={SCREEN_WIDTH ? {} : { flex: '3' }}>
                {usageData ?
                    <Card title={`Hourly Report`} icon='clipboard'>
                        <div ref={cardRef}>
                            <HeaderRow style={{ marginBottom: '3em' }}>
                                <Col>
                                    <Heading>Period</Heading>
                                    <HeaderRow>
                                        <span>Date: {planeDate(offsetUTC(reportDate))}</span>
                                    </HeaderRow>
                                </Col>
                                <CompanyLogo dim={6} />
                            </HeaderRow>

                            <ReportSegment>
                                <Col style={{ gap: '2em' }}>
                                    <HeaderRow>
                                        <Col>
                                            <HeaderRow>
                                                <b>Items:</b>
                                                <Row>{usageData.selectedItems.map(e => itemType.find(f => f.value === e).label).join(', ')}</Row>
                                            </HeaderRow>
                                        </Col>
                                    </HeaderRow>

                                    <TableDetails data={usageData} />

                                </Col>
                            </ReportSegment>
                        </div>
                    </Card>
                    : null}
            </Col>
        </Row>
    );
}

export default HourlyUseReport;